/**
 * The IK authoring path: drag a target → solve on the live bones → write canonical rotations
 * into the live pose.
 *
 * This is the piece that keeps SPEC's "critical serialization rule" true. The solver mutates
 * three.js bones (that is what makes the viewport update), but nothing about the IK *target*
 * ever reaches the clip: we immediately read the resulting rotations back in canonical space and
 * push them through the ordinary pose action, so an IK-authored limb is stored as two plain bone
 * rotation tracks and a runtime that has never heard of IK plays it back identically.
 *
 * Only the chain root and mid are written. The end bone (hand/foot) is deliberately untouched —
 * `solveChainOnBones` never rotates it (D22), so keying it here would freeze whatever FK rotation
 * the user had given it into every IK drag.
 *
 * Depends on three math only, so it is testable against a real `buildCanonicalSkeleton` fixture
 * with no WebGL context.
 */

import { Quaternion, Vector3 } from 'three';
import type { Object3D } from 'three';

import { restBendDirForChain, solveChainOnBones } from '../../rig/ik';
import { CANONICAL_PARENT } from '../../shared';
import type { CanonicalBoneId, IkChain, QuaternionTuple, Vec3Tuple } from '../../shared';

/** The slice of `BoundRig` the IK path needs. Structural, so tests can pass a real `BoundRig`. */
export interface IkRigSource {
  boneObject(id: CanonicalBoneId): Object3D | undefined;
  readCanonicalLocal(id: CanonicalBoneId): QuaternionTuple;
  worldPosition(id: CanonicalBoneId): Vec3Tuple;
  restJointWorldPositions(): Partial<Record<CanonicalBoneId, Vec3Tuple>>;
  /**
   * Current world orientation of a bone. Optional so a minimal stub still satisfies the
   * interface; when it (or `restWorldQuaternion`) is missing the bend direction is simply not
   * re-oriented, which is the pre-wave-5 behaviour.
   */
  worldQuaternion?(id: CanonicalBoneId): QuaternionTuple;
  /** Bind-pose world orientation of a bone (`BoundRig.restWorldQuaternion`). */
  restWorldQuaternion?(id: CanonicalBoneId): QuaternionTuple;
}

export interface ApplyIkArgs {
  rig: IkRigSource;
  chain: IkChain;
  /** Where the user dragged the handle to. */
  targetWorld: Vector3;
  /** Optional pole handle position; overrides the rest-derived bend direction. */
  poleWorld?: Vector3;
  /** Normally `store.actions.setLivePoseBone`. */
  setLivePoseBone: (
    bone: CanonicalBoneId,
    rotation: QuaternionTuple,
    options?: { coalesceKey?: string },
  ) => void;
}

export interface ApplyIkResult {
  /** False when the target was out of reach and the limb had to be clamped to full extension. */
  reached: boolean;
  /** Bones whose rotation was written — always `[root, mid]`, or empty when the chain is unbound. */
  written: CanonicalBoneId[];
}

const _bendDir = new Vector3();
const _delta = new Quaternion();
const _restInverse = new Quaternion();

/**
 * How much the chain's parent (shoulder / hip side of the torso) has been rotated away from the
 * bind pose, as `worldNow * inverse(worldRest)`.
 *
 * Walks up the canonical hierarchy until it finds a bone the rig actually drives, so a rig with
 * no shoulder still picks up the chest/hips rotation. Identity when the rig cannot report rest
 * world orientations.
 */
function parentRotationSinceRest(rig: IkRigSource, chain: IkChain, out: Quaternion): Quaternion {
  out.identity();
  if (!rig.worldQuaternion || !rig.restWorldQuaternion) return out;

  let id: CanonicalBoneId | null = CANONICAL_PARENT[chain.root];
  while (id && !rig.boneObject(id)) id = CANONICAL_PARENT[id];
  if (!id) return out;

  const now = rig.worldQuaternion(id);
  const rest = rig.restWorldQuaternion(id);
  _restInverse.set(rest[0], rest[1], rest[2], rest[3]).invert();
  return out.set(now[0], now[1], now[2], now[3]).multiply(_restInverse);
}

/**
 * World-space bend direction for a chain, derived from the rest pose (elbows pre-bent −Z, knees
 * +Z in the canonical rig), falling back to the chain's own `fallbackBendDir` when the rest limb
 * is dead straight.
 *
 * The rest direction is then carried into the *current* pose by the parent's rotation since the
 * bind pose: with the torso turned 180° about Y an elbow must still bend backwards relative to
 * the character, not relative to the world (wave-5 review finding 5).
 */
export function bendDirForChain(rig: IkRigSource, chain: IkChain, out = new Vector3()): Vector3 {
  const dir = restBendDirForChain(chain, rig.restJointWorldPositions());
  out.set(dir[0], dir[1], dir[2]);
  return out.applyQuaternion(parentRotationSinceRest(rig, chain, _delta));
}

/**
 * The undo coalesce key a whole IK drag shares. Must match `beginIkDrag` / `setIkTarget`
 * (`ik:<chain>`), or the group closes on the first pose write.
 */
export function ikCoalesceKey(chain: IkChain): string {
  return `ik:${chain.name}`;
}

/**
 * Solve one chain onto the live bones and push the result into the live pose.
 *
 * The caller is responsible for wrapping a continuous drag in
 * `beginIkDrag(chain)` / `endIkDrag()` so the whole gesture collapses into one undo entry.
 */
export function applyIkChain(args: ApplyIkArgs): ApplyIkResult {
  const { rig, chain, targetWorld, poleWorld, setLivePoseBone } = args;

  const rootBone = rig.boneObject(chain.root);
  const midBone = rig.boneObject(chain.mid);
  const endBone = rig.boneObject(chain.end);
  if (!rootBone || !midBone || !endBone) return { reached: false, written: [] };

  const { reached } = solveChainOnBones({
    rootBone,
    midBone,
    endBone,
    targetWorld,
    poleWorld,
    bendDirWorld: bendDirForChain(rig, chain, _bendDir),
  });

  // Read back in canonical space: this is the conversion that makes the pose portable.
  //
  // Both writes carry the chain's coalesce key so they merge into the `beginIkDrag(chain)` group
  // instead of closing it: with the default `pose:<bone>` key every solve of a drag became its
  // own undo entry, and one drag left hundreds of them behind (wave-5 review finding 1).
  const options = { coalesceKey: ikCoalesceKey(chain) };
  setLivePoseBone(chain.root, rig.readCanonicalLocal(chain.root), options);
  setLivePoseBone(chain.mid, rig.readCanonicalLocal(chain.mid), options);

  return { reached, written: [chain.root, chain.mid] };
}

/**
 * Where an IK handle should sit when it is not being dragged: on the chain's end joint.
 *
 * Used both to initialise a chain's target the first time IK is switched on and to keep inactive
 * handles glued to their hand/foot as the playhead scrubs, so they never appear to lag behind the
 * pose they are supposed to control.
 */
export function restingTargetForChain(rig: IkRigSource, chain: IkChain): Vec3Tuple {
  return rig.worldPosition(chain.end);
}
